import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import CinematicNav, { BackToTop } from '../components/landing/CinematicNav'
import CinematicFooter from '../components/landing/CinematicFooter'
import Reveal from '../components/landing/Reveal'
import EditorialFrame from '../components/showcase/EditorialFrame'
import ShowcaseLightbox from '../components/showcase/ShowcaseLightbox'
import { useActiveIntake } from '../hooks/useActiveIntake'
import {
  SHOWCASE_SECTIONS,
  WORK_FILTERS,
  disciplines,
  experienceFrames,
  heroFrame,
  publishedFrames,
  publishedWork,
} from '../data/showcaseCatalog'

const SCROLL_KEY = 'sma.showcase.scroll'
const FILTER_PARAM = {
  All: null,
  Photography: 'photography',
  Video: 'video',
  'Graphic Design': 'graphic-design',
  Animation: 'animation',
  Other: 'other',
}
const PARAM_FILTER = Object.fromEntries(
  Object.entries(FILTER_PARAM).filter(([, value]) => value).map(([label, value]) => [value, label])
)

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// Equal-height frames on a 12-column grid. Photos in an incomplete last row widen
// to fill it, so rows never have gaps. `cols` = photos per row at each breakpoint.
const SPAN = { base: { 12: 'col-span-12', 6: 'col-span-6', 4: 'col-span-4', 3: 'col-span-3' }, md: { 12: 'md:col-span-12', 6: 'md:col-span-6', 4: 'md:col-span-4', 3: 'md:col-span-3' }, lg: { 12: 'lg:col-span-12', 6: 'lg:col-span-6', 4: 'lg:col-span-4', 3: 'lg:col-span-3' } }
function spanFor(index, total, perRow) {
  const rest = total % perRow
  const inLastRow = rest && index >= total - rest
  return 12 / (inLastRow ? rest : perRow)
}
function gridSpan(index, total, cols) {
  return [
    SPAN.base[spanFor(index, total, cols.base)],
    SPAN.md[spanFor(index, total, cols.md)],
    SPAN.lg[spanFor(index, total, cols.lg)],
  ].join(' ')
}

function GridFrame({ src, alt, category, title, position, heightClass, onOpen, badge, className = '' }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`group block text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cinema-gold ${className}`}
    >
      <span className={`relative block overflow-hidden bg-cinema-charcoal ${heightClass}`}>
        <img
          src={src}
          alt={alt || ''}
          loading="lazy"
          decoding="async"
          onError={(event) => { event.currentTarget.style.visibility = 'hidden' }}
          className={`absolute inset-0 h-full w-full object-cover transition duration-700 ease-out motion-safe:group-hover:scale-[1.03] ${position || 'object-center'}`}
        />
        {badge && (
          <span className="absolute left-3 bottom-3 bg-black/60 px-2 py-1 text-[0.65rem] uppercase tracking-[0.2em] text-white font-sans">{badge}</span>
        )}
      </span>
      <span className="block pt-3">
        {category && <span className="text-[0.65rem] uppercase tracking-[0.18em] text-cinema-gold font-sans">{category}</span>}
        <span className="mt-1 block font-display text-lg sm:text-xl text-white font-light leading-snug">{title}</span>
      </span>
    </button>
  )
}

const FRAME_COLS = { base: 1, md: 2, lg: 3 }

function FrameRow({ frames, onOpen }) {
  if (!frames.length) return null
  return (
    <div className="mt-12 grid grid-cols-12 gap-x-6 gap-y-10">
      {frames.map((frame, index) => (
        <GridFrame
          key={frame.id || frame.src}
          src={frame.src}
          alt={frame.alt}
          category={frame.category}
          title={frame.caption}
          position={frame.position}
          heightClass="h-64 sm:h-72 lg:h-[22rem]"
          onOpen={() => onOpen(index)}
          className={gridSpan(index, frames.length, FRAME_COLS)}
        />
      ))}
    </div>
  )
}

export default function ShowcasePage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { intake, fallback } = useActiveIntake()
  const [scrolled, setScrolled] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [activeSection, setActiveSection] = useState('learn')
  const [activeDiscipline, setActiveDiscipline] = useState(disciplines.find((item) => item.image) || disciplines[0])
  const [lightbox, setLightbox] = useState(null)
  const [contactEmail, setContactEmail] = useState('synergymultimediaacademy@gmail.com')

  const hasIntake = Boolean(!fallback && intake && intake.status && !['none', 'closed', 'upcoming'].includes(intake.status))
  const applyCta = hasIntake && intake.hero?.primaryCta?.href
    ? intake.hero.primaryCta
    : { label: 'Apply to SMA', href: '/intake' }
  const work = useMemo(() => publishedWork(), [])
  const filter = PARAM_FILTER[searchParams.get('work')] || 'All'
  const visibleWork = filter === 'All' ? work : work.filter((item) => item.filter === filter)
  const featured = visibleWork.find((item) => item.featured) || visibleWork[0] || null
  const supporting = visibleWork.filter((item) => item !== featured)

  const learn = publishedFrames(experienceFrames.learn)
  const shoot = publishedFrames(experienceFrames.shoot)
  const edit = publishedFrames(experienceFrames.edit)
  const together = publishedFrames(experienceFrames.together)

  useEffect(() => {
    document.title = 'Creative Training & Student Work in Kampala | Synergy Multimedia Academy'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'See student work and life at Synergy Multimedia Academy in Kampala. Explore practical training in photography, videography, graphic design, animation, AI and multimedia production.'
      )
    }
  }, [])

  useEffect(() => {
    const saved = sessionStorage.getItem(SCROLL_KEY)
    if (!saved) return undefined
    sessionStorage.removeItem(SCROLL_KEY)
    const y = Number(saved)
    if (!Number.isFinite(y)) return undefined
    const id = window.requestAnimationFrame(() => window.scrollTo(0, y))
    return () => window.cancelAnimationFrame(id)
  }, [])

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40)
      setShowBackToTop(window.scrollY > 800)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const nodes = SHOWCASE_SECTIONS.map((section) => document.getElementById(section.id)).filter(Boolean)
    if (!nodes.length) return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (visible?.target?.id) setActiveSection(visible.target.id)
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0.15, 0.4] }
    )
    nodes.forEach((node) => observer.observe(node))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_URL || '/api'
    fetch(`${apiBase}/auth/public-settings`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.email && !data.email.startsWith('info@')) setContactEmail(data.email)
      })
      .catch(() => {})
  }, [])

  const setFilter = (label) => {
    const next = new URLSearchParams(searchParams)
    const value = FILTER_PARAM[label]
    if (value) next.set('work', value)
    else next.delete('work')
    setSearchParams(next, { replace: true })
  }

  const openProject = (slug) => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY))
    navigate(`/showcase/${slug}`)
  }

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>Creative Training & Student Work in Kampala | Synergy Multimedia Academy</title>
        <meta
          name="description"
          content="See student work and life at Synergy Multimedia Academy in Kampala. Explore practical training in photography, videography, graphic design, animation, AI and multimedia production."
        />
        <link rel="canonical" href="https://synergymultimediaacademy.com/showcase" />
        <meta property="og:title" content="See what happens when learning becomes making. | Synergy Multimedia Academy" />
        <meta
          property="og:description"
          content="Learn creative skills. Make real work. Build your future. Practical creative training in Kampala, Uganda."
        />
        <meta property="og:url" content="https://synergymultimediaacademy.com/showcase" />
      </Helmet>
      <div className="cinema-grain" aria-hidden="true" />
      <CinematicNav
        scrolled={scrolled}
        navOverHero={!scrolled}
        onNavigate={scrollToId}
        intake={intake}
        fallback={fallback}
      />

      <main>
        <header className="relative isolate flex min-h-[72svh] items-end overflow-hidden border-b border-white/10">
          <div className="absolute inset-0 -z-10" aria-hidden="true">
            <img
              src={heroFrame.src}
              alt=""
              fetchPriority="high"
              className={`h-full w-full object-cover ${heroFrame.position || 'object-center'}`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/50" />
            <div className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-black/90 to-transparent" />
          </div>
          <div className="w-full max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 pt-40 pb-16 sm:pt-48 sm:pb-20 lg:pb-24">
            <Reveal className="max-w-3xl">
              <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Showcase</p>
              <h1 className="mt-6 font-display text-4xl sm:text-6xl lg:text-[4.5rem] text-white font-light tracking-tight leading-[1.04]">
                See what happens when learning becomes making.
              </h1>
              <p className="mt-6 max-w-xl text-lg sm:text-xl text-white/85 font-sans leading-relaxed">
                Real classes, real shoots and real work by SMA students in Kampala.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <button type="button" onClick={() => scrollToId('show')} className="cinema-btn-primary">
                  See the work
                </button>
                <Link to={applyCta.href} className="cinema-btn-ghost bg-black/30 backdrop-blur-sm">
                  {applyCta.label}
                </Link>
              </div>
            </Reveal>
          </div>
        </header>

        <nav
          aria-label="Showcase sections"
          className="sticky top-16 sm:top-20 lg:top-24 z-30 border-b border-white/10 bg-cinema-black/90 backdrop-blur-sm"
        >
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 flex gap-6 overflow-x-auto py-3">
            {SHOWCASE_SECTIONS.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                onClick={(event) => {
                  event.preventDefault()
                  scrollToId(section.id)
                }}
                className={`shrink-0 min-h-11 inline-flex items-center text-[0.68rem] uppercase tracking-[0.22em] font-sans ${
                  activeSection === section.id ? 'text-white' : 'text-cinema-muted hover:text-white'
                }`}
                aria-current={activeSection === section.id ? 'true' : undefined}
              >
                {section.label}
              </a>
            ))}
          </div>
        </nav>

        <section id="experience" className="scroll-mt-32 border-b border-white/10">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-16 sm:py-24">
            <Reveal className="max-w-3xl">
              <h2 className="font-display text-4xl sm:text-6xl text-white font-light tracking-tight">
                This is more than a classroom.
              </h2>
              <p className="mt-6 text-base sm:text-lg text-cinema-soft font-sans leading-relaxed max-w-xl">
                SMA is where creative skills are learned by doing. Students don't only watch demonstrations or sit through theory. They practice, experiment, shoot, edit, design, collaborate and build real creative work.
              </p>
            </Reveal>
          </div>
        </section>

        <section id="learn" className="scroll-mt-32 border-b border-white/10">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-16 sm:py-24">
            <Reveal>
              <p className="text-[0.7rem] uppercase tracking-[0.22em] text-cinema-gold font-sans">01 — Learn</p>
              <h2 className="mt-3 max-w-3xl font-display text-4xl sm:text-6xl text-white font-light tracking-tight">
                Start with the craft.
              </h2>
              <p className="mt-5 max-w-xl text-sm sm:text-base text-cinema-muted font-sans leading-relaxed">
                Learn the skills behind professional creative work — from photography and video production to graphic design, animation and AI-powered creative workflows.
              </p>
            </Reveal>
            <FrameRow frames={learn} onOpen={(index) => setLightbox({ frames: learn, index })} />
          </div>
        </section>

        <section id="shoot" className="scroll-mt-32 border-b border-white/10">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-16 sm:py-24">
            <Reveal>
              <p className="text-[0.7rem] uppercase tracking-[0.22em] text-cinema-gold font-sans">02 — Shoot</p>
              <h2 className="mt-3 max-w-3xl font-display text-4xl sm:text-6xl text-white font-light tracking-tight">
                Take the learning outside the classroom.
              </h2>
              <p className="mt-5 max-w-xl text-sm sm:text-base text-cinema-muted font-sans leading-relaxed">
                Cameras. Lights. Sound. Direction. Production. Students put their skills into practice by working with real creative equipment and real production situations.
              </p>
            </Reveal>
            <FrameRow frames={shoot} onOpen={(index) => setLightbox({ frames: shoot, index })} />
          </div>
        </section>

        <section id="edit" className="scroll-mt-32 border-b border-white/10">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-16 sm:py-24">
            <Reveal>
              <p className="text-[0.7rem] uppercase tracking-[0.22em] text-cinema-gold font-sans">03 — Edit</p>
              <h2 className="mt-3 max-w-3xl font-display text-4xl sm:text-6xl text-white font-light tracking-tight">
                Turn raw material into finished work.
              </h2>
              <p className="mt-5 max-w-xl text-sm sm:text-base text-cinema-muted font-sans leading-relaxed">
                The creative process continues after the shoot. Students learn how to edit photographs, video, graphics and other media into work they can actually show.
              </p>
            </Reveal>
            <FrameRow frames={edit} onOpen={(index) => setLightbox({ frames: edit, index })} />
          </div>
        </section>

        <section id="create" className="scroll-mt-32 border-b border-white/10">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-6">
              <p className="text-[0.7rem] uppercase tracking-[0.22em] text-cinema-gold font-sans">04 — Create</p>
              <h2 className="mt-3 font-display text-4xl sm:text-6xl text-white font-light tracking-tight">
                Ideas become things you can see.
              </h2>
              <ul className="mt-10 border-t border-white/10">
                {disciplines.map((item) => {
                  const active = activeDiscipline?.id === item.id
                  const row = (
                    <>
                      <span className="font-display text-2xl sm:text-4xl font-light">{item.label}</span>
                      <span className={`block mt-1 text-sm font-sans ${active ? 'text-cinema-soft' : 'text-cinema-muted'}`}>
                        {item.copy}
                      </span>
                    </>
                  )
                  const className = `block w-full text-left py-4 border-b border-white/10 transition-colors min-h-11 ${
                    active ? 'text-white' : 'text-cinema-soft hover:text-white'
                  }`
                  if (!item.href) {
                    return (
                      <li key={item.id}>
                        <button type="button" className={className} onMouseEnter={() => item.image && setActiveDiscipline(item)} onFocus={() => item.image && setActiveDiscipline(item)}>
                          {row}
                        </button>
                      </li>
                    )
                  }
                  return (
                    <li key={item.id}>
                      <Link
                        to={item.href}
                        className={className}
                        onMouseEnter={() => item.image && setActiveDiscipline(item)}
                        onFocus={() => item.image && setActiveDiscipline(item)}
                      >
                        {row}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
            <div className="lg:col-span-6 lg:pt-16">
              {activeDiscipline?.image && (
                <EditorialFrame
                  frame={{
                    src: activeDiscipline.image,
                    alt: activeDiscipline.alt,
                    caption: activeDiscipline.label,
                    category: 'Creating',
                    ratio: '4 / 5',
                    position: 'object-center',
                  }}
                  onOpen={(frame) => setLightbox({ frames: [frame], index: 0 })}
                />
              )}
            </div>
          </div>
        </section>

        <section id="show" className="scroll-mt-32 border-b border-white/10">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-16 sm:py-24">
            <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
              <div>
                <p className="text-[0.7rem] uppercase tracking-[0.22em] text-cinema-gold font-sans">05 — Show</p>
                <h2 className="mt-3 font-display text-4xl sm:text-6xl text-white font-light tracking-tight">Made at SMA.</h2>
                <p className="mt-4 text-sm sm:text-base text-cinema-muted font-sans">The work is the proof.</p>
              </div>
            </Reveal>

            <div className="mt-8 flex gap-5 overflow-x-auto border-b border-white/10" role="tablist" aria-label="Filter student work">
              {WORK_FILTERS.map((label) => (
                <button
                  key={label}
                  type="button"
                  role="tab"
                  aria-selected={filter === label}
                  onClick={() => setFilter(label)}
                  className={`shrink-0 min-h-11 text-[0.68rem] uppercase tracking-[0.18em] font-sans ${
                    filter === label ? 'text-white' : 'text-cinema-muted hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {work.length === 0 ? (
              <div className="mt-14 max-w-xl">
                <h3 className="font-display text-3xl text-white font-light">The work is on its way.</h3>
                <p className="mt-3 text-sm text-cinema-muted font-sans leading-relaxed">
                  Student work from SMA will appear here as new projects are completed and published.
                </p>
                <div className="mt-6 flex flex-wrap gap-6">
                  <button type="button" onClick={() => scrollToId('experience')} className="cinema-btn-text text-cinema-gold">
                    Explore SMA <span aria-hidden="true">→</span>
                  </button>
                  <Link to="/programs" className="cinema-btn-text text-cinema-gold">
                    Explore Courses <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>
            ) : visibleWork.length === 0 ? (
              <div className="mt-14 max-w-xl">
                <h3 className="font-display text-3xl text-white font-light">More work is coming.</h3>
                <p className="mt-3 text-sm text-cinema-muted font-sans leading-relaxed">
                  We're building this collection with work made by students at SMA.
                </p>
                <button type="button" onClick={() => setFilter('All')} className="cinema-btn-text mt-6 text-cinema-gold">
                  View all work <span aria-hidden="true">→</span>
                </button>
              </div>
            ) : (
              <div className="mt-10">
                {featured && (
                  <GridFrame
                    src={featured.src}
                    alt={featured.alt}
                    category={featured.filter}
                    title={featured.creator ? `${featured.title} · by ${featured.creator}` : featured.title}
                    heightClass="h-72 sm:h-[26rem] lg:h-[32rem]"
                    badge={featured.youtubeId ? 'Play' : null}
                    onOpen={() => openProject(featured.slug)}
                    className="w-full"
                  />
                )}
                {supporting.length > 0 && (
                  <div className="mt-10 grid grid-cols-12 gap-x-6 gap-y-10">
                    {supporting.map((item, index) => (
                      <GridFrame
                        key={item.slug}
                        src={item.src}
                        alt={item.alt}
                        category={item.filter}
                        title={item.title}
                        heightClass="h-56 sm:h-64 lg:h-72"
                        badge={item.youtubeId ? 'Play' : null}
                        onOpen={() => openProject(item.slug)}
                        className={gridSpan(index, supporting.length, { base: 2, md: 2, lg: 4 })}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <section id="together" className="scroll-mt-32 border-b border-white/10">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-16 sm:py-24">
            <Reveal>
              <h2 className="max-w-3xl font-display text-4xl sm:text-6xl text-white font-light tracking-tight">
                Learning happens together.
              </h2>
              <p className="mt-5 max-w-md text-sm sm:text-base text-cinema-muted font-sans leading-relaxed">
                Students review work, set up equipment, and figure the next shot out in the same room.
              </p>
            </Reveal>
            <FrameRow frames={together} onOpen={(index) => setLightbox({ frames: together, index })} />
          </div>
        </section>

        <section id="kampala" className="scroll-mt-32 border-b border-white/10">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-16 sm:py-24 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <h2 className="font-display text-4xl sm:text-5xl text-white font-light tracking-tight">
                Creative training in Kampala
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 text-sm sm:text-base text-cinema-soft font-sans leading-relaxed space-y-4">
              <p>
                Synergy Multimedia Academy trains people in Kampala, Uganda, who want to learn creative work by making it. The programs cover photography, videography, graphic design, animation, AI tools for creatives, and multimedia production.
              </p>
              <p>
                This page is for prospective students who want to see what the academy looks like in practice: class, equipment, editing, and the work that comes out of it.
              </p>
            </div>
          </div>
        </section>

        <section className="border-b border-white/10">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <h2 className="font-display text-4xl sm:text-6xl text-white font-light tracking-tight">
                Your turn to make something.
              </h2>
              <p className="mt-5 max-w-md text-sm sm:text-base text-cinema-muted font-sans leading-relaxed">
                Learn the craft. Practice the work. Build something worth showing.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/programs" className="cinema-btn-primary">Explore Courses</Link>
                <Link to="/register" className="cinema-btn-ghost">Apply to SMA</Link>
              </div>
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <img
                src="/Gallery/Academy/Learning In Action/965.jpg"
                alt="A student silhouetted against the sunset beside a camera tripod"
                loading="lazy"
                className="w-full aspect-[4/5] object-cover object-[40%_center]"
              />
            </div>
          </div>
        </section>

        <CinematicFooter contactEmail={contactEmail} closing={false} />
      </main>

      <BackToTop visible={showBackToTop} />
      {lightbox && (
        <ShowcaseLightbox
          frames={lightbox.frames}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndex={(index) => setLightbox((current) => (current ? { ...current, index } : current))}
        />
      )}
    </div>
  )
}
